// src/components/organisation/renderers/HeroSectionRenderer.jsx
import React from 'react';
import { ChevronDown, Play, ArrowRight, ExternalLink } from 'lucide-react';

export const HeroSectionRenderer = ({ 
  data, 
  bookData, 
  customStyling = {}, 
  onInteraction 
}) => {
  const {
    // Text content
    preHeading = '',
    heading = '',
    subHeading = '',
    description = '',
    
    // Call to action
    primaryButtonText = '',
    primaryButtonUrl = '',
    secondaryButtonText = '',
    secondaryButtonUrl = '',
    
    // Media
    backgroundImage = '',
    image = '', // Main image for split layouts
    imageAlt = '',
    videoUrl = '',
    
    // Layout & styling
    layout = 'center', // center, split, left
    showScrollIndicator = false,
    
    // Additional settings
    enabled = true
  } = data || {};

  // Don't render if disabled
  if (!enabled) return null;

  // Handle interaction tracking
  const trackInteraction = (action, details = {}) => {
    if (onInteraction) {
      onInteraction({
        type: 'hero_interaction',
        action,
        section: 'hero',
        sectionId: data?.id,
        ...details
      });
    }
  };

  // Handle CTA clicks
  const handleCTAClick = (url, buttonType) => {
    trackInteraction('cta_click', { url, buttonType });
    if (url) {
      if (url.startsWith('http') || url.startsWith('https')) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = url;
      }
    }
  };

  // Handle video play
  const handleVideoPlay = () => {
    trackInteraction('video_play', { videoUrl });
    if (videoUrl) {
      window.open(videoUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Get background styles
  const getBackgroundStyles = () => {
    const base = {
      backgroundColor: customStyling?.primaryColor || '#1e40af',
      ...customStyling
    };

    if (backgroundImage) {
      return {
        ...base,
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }

    return base;
  };

  // Render content based on layout
  const renderContent = () => {
    const textContent = (
      <div className={`text-content ${layout === 'center' ? 'text-center' : 'text-left'} space-y-6`}>
        {/* Pre-heading */}
        {preHeading && (
          <div className="pre-heading">
            <span className="inline-block px-4 py-2 bg-white bg-opacity-20 text-white text-sm font-medium rounded-full backdrop-blur-sm">
              {preHeading}
            </span>
          </div>
        )}

        {/* Main heading */}
        {heading && (
          <h1 className="heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
            {heading}
          </h1>
        )}

        {/* Sub-heading */}
        {subHeading && (
          <h2 className="sub-heading text-xl md:text-2xl lg:text-3xl text-gray-200 font-medium">
            {subHeading}
          </h2>
        )}

        {/* Description */}
        {description && (
          <p className="description text-lg md:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {description}
          </p>
        )}

        {/* Action buttons */}
        {(primaryButtonText || secondaryButtonText || videoUrl) && (
          <div className="actions-container space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
              {/* Primary CTA */}
              {primaryButtonText && primaryButtonUrl && (
                <button
                  onClick={() => handleCTAClick(primaryButtonUrl, 'primary')}
                  className="primary-cta bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
                >
                  {primaryButtonText}
                  <ArrowRight className="h-5 w-5" />
                </button>
              )}

              {/* Secondary CTA */}
              {secondaryButtonText && secondaryButtonUrl && (
                <button
                  onClick={() => handleCTAClick(secondaryButtonUrl, 'secondary')}
                  className="secondary-cta border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-all duration-200 flex items-center gap-2"
                >
                  {secondaryButtonText}
                  <ExternalLink className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Video button */}
            {videoUrl && (
              <button
                onClick={handleVideoPlay}
                className="video-cta flex items-center gap-3 text-white hover:text-gray-200 transition-colors group"
              >
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center group-hover:bg-opacity-30 transition-all backdrop-blur-sm">
                  <Play className="h-5 w-5 ml-1" fill="currentColor" />
                </div>
                <span className="font-medium">Watch Video</span>
              </button>
            )}
          </div>
        )}
      </div>
    );

    // Handle different layouts
    switch (layout) {
      case 'split':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              {textContent}
            </div>
            {image && (
              <div className="order-1 lg:order-2">
                <img 
                  src={image} 
                  alt={imageAlt || heading || 'Hero image'}
                  className="w-full h-auto max-w-lg mx-auto rounded-xl shadow-2xl"
                />
              </div>
            )}
          </div>
        );
      
      case 'left':
        return (
          <div className="max-w-4xl">
            {textContent}
          </div>
        );
      
      default: // center
        return (
          <div className="max-w-5xl mx-auto">
            {textContent}
          </div>
        );
    }
  };

  return (
    <section 
      className="hero-section relative min-h-[90vh] flex items-center justify-center px-4 py-16"
      style={getBackgroundStyles()}
    >
      {/* Content container */}
      <div className="container mx-auto relative z-10">
        {renderContent()}
      </div>

      {/* Scroll indicator */}
      {showScrollIndicator && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="scroll-indicator animate-bounce">
            <ChevronDown className="h-6 w-6 text-white opacity-75" />
          </div>
        </div>
      )}

      {/* Overlay for better text readability when background image is used */}
      {backgroundImage && (
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      )}
    </section>
  );
};